import type { ReactElement } from 'react';
import { normalisiereMarkdown } from '@mcikm-vote/shared';

import { RichText } from './RichText';

/**
 * Editor für Rechtstexte in Markdown (ÄA-5).
 *
 * **Reines Markdown, keine formatierte Bearbeitungsfläche.** Bis Version 1.2.0 gab es zusätzlich
 * einen WYSIWYG-Modus mit `contentEditable` und `document.execCommand`; der Auftraggeber hat ihn
 * nach der Erprobung wieder verworfen. Was bleibt, ist einfacher und verlässlicher: Der
 * Administrator sieht genau den Text, der gespeichert wird, und die Vorschau daneben zeigt, was
 * daraus wird.
 *
 * Der Wegfall hat einen willkommenen Nebeneffekt: Die Anwendung schreibt jetzt **nirgends** mehr
 * in `innerHTML` — auch nicht flüchtig im Browser. Siehe Entscheidung E38 in
 * `docs/ARCHITECTURE.md`; `FormatierterText.test.ts` hält das über den gesamten Quelltext fest.
 *
 * Eine vorhandene `.md`-Datei lässt sich einfügen oder über die Schaltfläche einlesen.
 *
 * Kanonisiert wird **nicht** beim Tippen — das räumte jede noch unvollständige Auszeichnung sofort
 * weg. Verbindlich geschieht es serverseitig beim Speichern (§25.2).
 */

/** Höhe der Vorschau, bevor sie scrollt. Hält die Personalisierungsseite überschaubar. */
const VORSCHAU_HOEHE = 'max-h-80';

export function RechtstextEditor({
  name,
  beschriftung,
  wert,
  grenze,
  onAendern,
}: {
  name: string;
  beschriftung: string;
  /** Markdown. */
  wert: string;
  grenze: number;
  onAendern: (wert: string) => void;
}): ReactElement {
  /**
   * Liest eine Markdown-Datei ein.
   *
   * Der Inhalt **ersetzt** den bisherigen Text — deshalb die Rückfrage, sobald schon etwas
   * dasteht. Gelesen wird ausschließlich im Browser; die Datei wird nicht hochgeladen.
   */
  function leseDatei(datei: File): void {
    if (wert.trim() !== '' && !window.confirm('Der vorhandene Text wird ersetzt. Fortfahren?')) {
      return;
    }

    const leser = new FileReader();
    leser.onload = () => {
      const inhalt = typeof leser.result === 'string' ? leser.result : '';
      onAendern(normalisiereMarkdown(inhalt.slice(0, grenze)));
    };
    leser.readAsText(datei);
  }

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={name} className="text-sm font-medium">
        {beschriftung}
      </label>

      <textarea
        id={name}
        name={name}
        value={wert}
        rows={16}
        maxLength={grenze}
        aria-describedby={`${name}-hilfe`}
        onChange={(ereignis) => {
          onAendern(ereignis.target.value);
        }}
        className="border-rand bg-hintergrund rounded-md border px-3 py-2 font-mono text-sm"
      />

      <p id={`${name}-hilfe`} className="text-text-gedaempft text-xs">
        Markdown: Überschriften mit <code># </code> bis <code>###### </code>, <code>**fett**</code>,{' '}
        <code>*kursiv*</code>, Aufzählungen mit <code>- </code>, nummerierte Listen mit{' '}
        <code>1. </code>, Links als <code>[Text](https://…)</code> und E-Mail-Adressen als{' '}
        <code>[E-Mail](mailto:info@verein.de)</code>. Ein Zeilenumbruch bleibt erhalten — eine
        Anschrift steht also untereinander; eine Leerzeile beginnt einen neuen Absatz. HTML wird
        nicht ausgewertet, sondern als Text angezeigt.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs">
          <span className="text-text-gedaempft">Markdown-Datei einlesen:</span>
          <input
            type="file"
            accept=".md,.markdown,text/markdown,text/plain"
            className="text-xs"
            onChange={(ereignis) => {
              const datei = ereignis.target.files?.[0];
              if (datei !== undefined) {
                leseDatei(datei);
              }
              // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
              ereignis.target.value = '';
            }}
          />
        </label>

        <p className="text-text-gedaempft text-xs">
          {wert.length} von {grenze} Zeichen
        </p>
      </div>

      <div className="border-rand mt-2 rounded-xl border p-3">
        <h3 className="mb-3 text-sm font-medium">Vorschau</h3>
        {/* Feste Höhe mit eigenem Bildlauf: Ohne sie wüchse die Personalisierungsseite mit jedem
            Absatz des Impressums, und die Abschnitte darunter wären kaum noch erreichbar. */}
        <div
          className={`bg-hintergrund text-text border-rand ${VORSCHAU_HOEHE} overflow-y-auto rounded-lg border p-4`}
        >
          {wert.trim() === '' ? (
            <p className="text-text-gedaempft text-sm">Noch kein Text eingegeben.</p>
          ) : (
            <RichText markdown={wert} />
          )}
        </div>
      </div>
    </div>
  );
}
